import { createClient } from '@supabase/supabase-js';
import React, { useState, useEffect, useRef } from 'react';

import Gallery from './components/Gallery';
import MediaViewer from './components/MediaViewer';
import PasswordGate from './components/PasswordGate';
import UploadForm from './components/UploadForm';
import { transformImage } from './utils/imageTransform';

// Types
export type MediaItem = {
  id: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  content_type: string;
  created_at: string;
};

const App: React.FC = () => {
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [isTestMode, setIsTestMode] = useState<boolean>(false);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreItems, setHasMoreItems] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [showUploadForm, setShowUploadForm] = useState<boolean>(false);
  const [slideshowActive, setSlideshowActive] = useState<boolean>(false);
  const [slideshowIntervalId, setSlideshowIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const headerRef = useRef<HTMLDivElement>(null);

  // Initialize Supabase client
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
  const supabase = createClient(supabaseUrl, supabaseKey);
  const bucketName = process.env.REACT_APP_STORAGE_BUCKET || 'media';
  const environment = process.env.REACT_APP_ENV || 'development';
  const ITEMS_PER_PAGE = parseInt(process.env.REACT_APP_ITEMS_PER_PAGE || '15');

  // Check if the app is in test mode
  useEffect(() => {
    const testMode = process.env.REACT_APP_TEST_MODE === 'true';
    setIsTestMode(testMode);
    
    // Auto-authenticate in test mode for development
    if (testMode) {
      setAuthenticated(true);
    }
  }, []);

  // Fetch media items when authenticated
  useEffect(() => {
    const alreadyAuthenticated = localStorage.getItem('authenticated')
    if (alreadyAuthenticated) {
      setAuthenticated(true);
    }
    if (authenticated) {
      fetchMediaItems(1, true);
    }
  }, [authenticated]);
  
  // Function to load more items when user scrolls
  const loadMoreItems = async () => {
    if (isLoadingMore || !hasMoreItems) {
      return;
    }
    const nextPage = currentPage + 1;
    await fetchMediaItems(nextPage, false);
  };

  // Cleanup slideshow on unmount
  useEffect(() => {
    return () => {
      if (slideshowIntervalId) {
        clearInterval(slideshowIntervalId);
      }
    };
  }, [slideshowIntervalId]);

  // Handle scroll for sticky header and scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const scrollPosition = window.scrollY;

        // Make header sticky when scrolled past the header
        setIsSticky(scrollPosition > headerHeight);

        // Show scroll-to-top button when scrolled past 300px
        setShowScrollTop(scrollPosition > 300);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Function to scroll back to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const fetchMediaItems = async (page = 1, resetItems = true) => {
    try {
      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      // Reset state when loading the first page
      if (resetItems) {
        setMediaItems([]);
        setCurrentPage(1);
        setHasMoreItems(true);
      }
      
      console.log(`Fetching media items from bucket: ${bucketName}, page: ${page}, limit: ${ITEMS_PER_PAGE}`);
      
      // Calculate offset for pagination
      const offset = (page - 1) * ITEMS_PER_PAGE;
      
      // List files in the bucket with pagination
      const { data, error } = await supabase.storage
        .from(bucketName)
        .list(environment, {
          limit: ITEMS_PER_PAGE,
          offset: offset,
          sortBy: { column: 'created_at', order: 'desc' }
        });
      
      if (error) {
        console.error("Supabase list error:", error);
        throw new Error(error.message);
      }
      
      console.log(`Raw data from Supabase (page ${page}):`, data);
      
      // Determine if there are more items to load
      // Only consider it "more" if we got the full page size 
      const hasMore = data && data.length === ITEMS_PER_PAGE;
      setHasMoreItems(hasMore);
      
      if (data && data.length > 0) {
        const validFiles = data.filter(item => item.name && !item.name.startsWith("."));
        const mediaData = await Promise.all(
          validFiles.map(async (item) => {
            // Determine if it's a video based on extension or metadata
            const isVideo =
              (item.metadata?.mimetype?.startsWith('video/')) ||
              (item.name.match(/\.(mp4|mov|avi|wmv|flv|mkv)$/i) !== null);
            
            // Use item.id for consistent ID, fallback to name + creation time if no ID
            // This ensures we don't get duplicate entries when adding more items
            const itemId = item.id || `file-${item.name}-${item.created_at}`;

            const filePath = environment + '/' + item.name;

            // Generate full resolution URL
            const { data: publicUrl } = supabase.storage
              .from(bucketName)
              .getPublicUrl(filePath);

            // Generate thumbnail locally for images (not videos)
            let thumbnailUrl = publicUrl.publicUrl;
            if (!isVideo && item.name.match(/\.(jpe?g|png|gif|webp|avif|tiff)$/i)) {
              try {
                thumbnailUrl = await transformImage(publicUrl.publicUrl, {
                  width: 300,
                  height: 200,
                  quality: 0.65,
                  resize: 'cover'
                });
              } catch (error) {
                console.error('Error transforming image:', error);
                // Fallback to full resolution image if transformation fails
                thumbnailUrl = publicUrl.publicUrl;
              }
            }

            return {
              id: itemId,
              name: item.name,
              url: publicUrl.publicUrl,
              thumbnailUrl: thumbnailUrl,
              content_type: isVideo ? 'video' : 'image',
              created_at: item.created_at || new Date().toISOString(),
            };
          })
        );
        
        // Update state with new media items, ensuring no duplicates
        setMediaItems(prevItems => {
          if (resetItems) {
            return mediaData;
          } else {
            // Create a map of existing item IDs for quick lookup
            const existingIds = new Set(prevItems.map(item => item.id));
            
            // Filter out any duplicates from the new data
            const uniqueNewItems = mediaData.filter(item => !existingIds.has(item.id));
            
            console.log(`Adding ${uniqueNewItems.length} new unique items (filtered out ${mediaData.length - uniqueNewItems.length} duplicates)`);
            
            // If we filtered everything out, there are no more unique items to load
            if (uniqueNewItems.length === 0 && mediaData.length > 0) {
              setHasMoreItems(false);
              console.log("All new items were duplicates, reached end of unique content");
            }
            
            return [...prevItems, ...uniqueNewItems];
          }
        });
        
        // Update current page
        setCurrentPage(page);
      } else if (page === 1) {
        // If no data or empty array on first page, set empty media items
        console.log("No media items found in the bucket");
        setMediaItems([]);
        setHasMoreItems(false);
      }
    } catch (error) {
      console.error("Error in fetchMediaItems:", error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handlePasswordSubmit = (password: string): boolean => {
    const correctPassword = process.env.REACT_APP_PASSWORD || '';
    
    const isCorrect = password.toLowerCase() === correctPassword.toLowerCase() || isTestMode;
    if (isCorrect) {
      localStorage.setItem('authenticated', 'true');
    }
    setAuthenticated(isCorrect);
    return isCorrect;
  };

  const handleMediaSelect = (media: MediaItem) => {
    // Stop slideshow if running when manually selecting media
    if (slideshowActive) {
      stopSlideshow();
    }
    setSelectedMedia(media);
  };

  const handleMediaNavigate = (mediaId: string) => {
    const media = mediaItems.find(item => item.id === mediaId);
    if (media) {
      setSelectedMedia(media);
    }
  };

  const handleCloseViewer = () => {
    setSelectedMedia(null);
    stopSlideshow();
  };

  const handleDeleteMedia = async (mediaId: string): Promise<boolean> => {
    try {
      const mediaToDelete = mediaItems.find(item => item.id === mediaId);

      if (!mediaToDelete) {
        console.error("Media not found:", mediaId);
        return false;
      }

      // Delete file from storage bucket
      const { error } = await supabase.storage
        .from(bucketName)
        .remove([environment+ '/' + mediaToDelete.name]);

      if (error) {
        console.error("Error deleting media: ", error);
        return false;
      }

      // Update local state (remove the deleted item)
      setMediaItems(prevItems => prevItems.filter(item => item.id !== mediaId));

      // Close viewer if the deleted media was selected
      if (selectedMedia && selectedMedia.id === mediaId) {
        setSelectedMedia(null);
      }

      return true;
    } catch (error) {
      console.error("Error in handleDeleteMedia:", error);
      return false;
    }
  };

  const handleUploadComplete = () => {
    setShowUploadForm(false);
    // Reset pagination and fetch the first page
    fetchMediaItems(1, true);
  };

  const toggleUploadForm = () => {
    setShowUploadForm(!showUploadForm);
  };

  // Slideshow functionality
  const startSlideshow = () => {
    if (mediaItems.length === 0) {
      return;
    }
    // Start with first item or current selection
    if (!selectedMedia) {
      setSelectedMedia(mediaItems[0]);
    }
    
    setSlideshowActive(true);
    
    // Set interval to change media every 5 seconds
    const intervalId = setInterval(() => {
      setSelectedMedia(currentMedia => {
        if (!currentMedia) {
           return mediaItems[0];
        }
        const currentIndex = mediaItems.findIndex(item => item.id === currentMedia.id);
        const nextIndex = (currentIndex + 1) % mediaItems.length;
        return mediaItems[nextIndex];
      });
    }, 5000);
    
    setSlideshowIntervalId(intervalId);
  };

  const stopSlideshow = () => {
    if (slideshowIntervalId) {
      clearInterval(slideshowIntervalId);
      setSlideshowIntervalId(null);
    }
    setSlideshowActive(false);
  };

  const toggleSlideshow = () => {
    if (slideshowActive) {
      stopSlideshow();
    } else {
      startSlideshow();
    }
  };

  if (!authenticated) {
    return <PasswordGate onSubmit={handlePasswordSubmit} isTestMode={isTestMode} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Sticky header that appears when scrolled past the original header */}
      {isSticky && !showUploadForm && (
        <div className="fixed top-0 left-0 right-0 bg-white shadow-md z-40 py-2 px-4">
          <div className="max-w-7xl mx-auto flex flex-row justify-between items-center">
            <div className="text-lg font-medium text-gray-800 hidden sm:block">Daniel & Anthea</div>
            <div className="flex flex-row justify-center sm:justify-end w-full sm:w-auto gap-2 sm:space-x-4">
              <button
                onClick={toggleSlideshow}
                className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-md font-medium focus:outline-none text-sm ${
                  slideshowActive
                    ? "bg-red-500 hover:bg-red-600 text-white"
                    : "bg-black hover:bg-blue-600 text-white"
                }`}
              >
                {slideshowActive ? "Stop Slideshow" : "Play Slideshow"}
              </button>

              <button
                onClick={toggleUploadForm}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-blue-500 hover:bg-success-dark text-white font-medium rounded-md transition duration-150 ease-in-out text-sm"
              >
                Upload Media
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main header - this is what appears at the top initially */}
      <header ref={headerRef} className="mb-6 sm:mb-8 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
        <div className="px-8 py-8 pb-0 text-black relative">
            <div className="items-center">
              <h2 className="text-l font-Bona-Nova leading-[1.5] tracking-widest text-center text-black">Daniel & Anthea</h2>
            </div>
        </div>
        <div className="px-8 pb-2 text-black relative">
            <div className="items-center mb-2">
              <h2 className="text-5xl font-Brittany-Signature leading-[1.5] text-center text-black">Wedding Gallery</h2>
            </div>
        </div>
        
        {/* Only show action buttons when upload form is not displayed */}
        {!showUploadForm && (
          <div className="flex flex-row justify-center sm:justify-end w-full sm:w-auto gap-2 sm:space-x-4">
            <button
              onClick={toggleSlideshow}
              className={`flex-1 sm:flex-initial px-3 sm:px-4 py-3 sm:py-2 rounded-md font-medium focus:outline-none text-sm sm:text-base ${
                slideshowActive 
                  ? "bg-red-500 hover:bg-red-600 text-white"
                  : "bg-black hover:bg-blue-600 text-white"
              }`}
            >
              {slideshowActive ? "Stop Slideshow" : "Play Slideshow"}
            </button>
            
            <button
              onClick={toggleUploadForm}
              className="flex-1 sm:flex-initial px-3 sm:px-4 py-3 sm:py-2 bg-blue-500 hover:bg-success-dark text-white font-medium rounded-md transition duration-150 ease-in-out text-sm sm:text-base"
            >
              Upload Media
            </button>
          </div>
        )}
      </header>
      
      <main className="min-h-[70vh]">
        {showUploadForm && (
          <div className="mb-8">
            <UploadForm 
              supabase={supabase} 
              bucketName={bucketName}
              environment={environment}
              onUploadComplete={handleUploadComplete} 
            />
          </div>
        )}
        
        {isLoading ? (
          <div className="text-center py-8 px-4 my-8 rounded-lg bg-gray-100 text-gray-600">
            Loading media...
          </div>
        ) : error ? (
          <div className="text-center py-8 px-4 my-8 rounded-lg bg-red-100 text-red-800">
            Error: {error}
          </div>
        ) : (
          <Gallery 
            mediaItems={mediaItems} 
            onMediaSelect={handleMediaSelect}
            isLoading={isLoadingMore}
            hasMore={hasMoreItems}
            onLoadMore={loadMoreItems}
          />
        )}
        
        {selectedMedia && (
          <MediaViewer 
            media={selectedMedia}
            mediaItems={mediaItems}
            onClose={handleCloseViewer}
            onNavigate={handleMediaNavigate}
            slideshowActive={slideshowActive}
            onToggleSlideshow={toggleSlideshow}
            onDelete={handleDeleteMedia}
          />
        )}

        {/* Scroll to top button */}
        {showScrollTop && (
          <button 
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-white shadow-lg text-gray-800 flex items-center justify-center hover:bg-gray-100 focus:outline-none transition-all duration-300 z-40"
            aria-label="Scroll to top"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
            </svg>
          </button>
        )}
      </main>
    </div>
  );
};

export default App;